import { createAsyncThunk, createSlice, miniSerializeError, SerializedError } from "@reduxjs/toolkit";
import { initialResult } from "src/data/mockData";
import { AnalyzedResult, Result } from "src/data/types";
import { apiRequest } from "src/utils/apiRequest";
import { LOADING_STATE } from "src/constants/constants";

type UpdateResultPayload = {
  inspection_id: string;
  question_id: string;
  formData: FormData;
};

export type InspectionResultsState = {
  inspectionResults: Result[];
  inspectionResult: Result;
  currentAnalyzedResult: AnalyzedResult;
  evidenceSample: File | string;
  
  // Loading states
  analyzeEvidenceLoading: LOADING_STATE;
  analyzeEvidenceAsyncLoading: LOADING_STATE;
  updateResultLoading: LOADING_STATE;
  
  // Error states
  analyzeEvidenceError: SerializedError | null;
  analyzeEvidenceAsyncError: SerializedError | null;
  updateResultError: SerializedError | null;
};

const initialState: InspectionResultsState = {
  inspectionResults: [],
  inspectionResult: initialResult,
  currentAnalyzedResult: {
    aiReasoning: "",
    aiResult: "",
    analysisTimestamp: "",
  },
  evidenceSample: "",
  
  // Initialize loading states
  analyzeEvidenceLoading: "idle",
  analyzeEvidenceAsyncLoading: "idle",
  updateResultLoading: "idle",
  
  // Initialize error states
  analyzeEvidenceError: null,
  analyzeEvidenceAsyncError: null,
  updateResultError: null,
};

export const analyzeEvidence = createAsyncThunk(
  "inspectionResults/analyzeEvidence",
  async (analyzePayload: FormData, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/analyze-evidence`,
        method: "POST",
        data: analyzePayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to analyze evidence");
    }
  }
);

export const updateInspectionResult = createAsyncThunk(
  "inspectionResults/updateInspectionResult",
  async (updateResultPayload: UpdateResultPayload, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${updateResultPayload.inspection_id}/questions/${updateResultPayload.question_id}/result`,
        method: "PUT",
        data: updateResultPayload.formData,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update result");
    }
  }
);

export const analyzeEvidenceAsync = createAsyncThunk(
  "inspectionResults/analyzeEvidenceAsync",
  async (analyzePayload: FormData, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/analyze-evidence-async`,
        method: "POST",
        data: analyzePayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to analyze evidence async");
    }
  }
);

/** Inspection Results Slice */
const { reducer, actions } = createSlice({
  name: "inspectionResults",
  initialState,
  reducers: {
    setInspectionResults: (state, { payload }) => {
      state.inspectionResults = payload;
    },
    updateInspectionResults: (state, { payload }: { payload: Result }) => {
      const newResultIndex = state.inspectionResults.findIndex(
        (result) => result.questionId === payload.questionId
      );
      if (newResultIndex !== -1) {
        state.inspectionResults[newResultIndex] = payload;
      } else {
        state.inspectionResults.push(payload);
      }
    },
    setInspectionResult: (state, { payload }) => {
      state.inspectionResult = payload;
    },
    resetCurrentAnalyzedResult: (state) => {
      state.currentAnalyzedResult = {
        aiReasoning: "",
        aiResult: "",
        analysisTimestamp: "",
      };
    },
    setCurrentAnalyzedResult: (state, { payload }) => {
      state.currentAnalyzedResult = payload;
    },
    resetInspectionResults: (state) => {
      state.inspectionResults = [];
      state.inspectionResult = initialResult;
      state.currentAnalyzedResult = {
        aiReasoning: "",
        aiResult: "",
        analysisTimestamp: "",
      };
      state.evidenceSample = "";
      state.analyzeEvidenceLoading = "idle";
      state.analyzeEvidenceAsyncLoading = "idle";
      state.updateResultLoading = "idle";
      state.analyzeEvidenceError = null;
      state.analyzeEvidenceAsyncError = null;
      state.updateResultError = null;
    },
  },
  extraReducers: (builder) => {
    // analyzeEvidence
    builder.addCase(analyzeEvidence.pending, (state) => {
      state.analyzeEvidenceLoading = "pending";
      state.analyzeEvidenceError = null;
    });
    builder.addCase(analyzeEvidence.fulfilled, (state, { payload }) => {
      state.currentAnalyzedResult = payload;
      state.analyzeEvidenceLoading = "fulfilled";
    });
    builder.addCase(analyzeEvidence.rejected, (state, { error }) => {
      state.analyzeEvidenceLoading = "rejected";
      state.analyzeEvidenceError = miniSerializeError(error);
    });
    
    // updateInspectionResult
    builder.addCase(updateInspectionResult.pending, (state) => {
      state.updateResultLoading = "pending";
      state.updateResultError = null;
    });
    builder.addCase(updateInspectionResult.fulfilled, (state, { payload }) => {
      state.updateResultLoading = "fulfilled";
      // TODO: UPDATE THE STATE HERE
    });
    builder.addCase(updateInspectionResult.rejected, (state, { error }) => {
      state.updateResultLoading = "rejected";
      state.updateResultError = miniSerializeError(error);
    });
    
    // analyzeEvidenceAsync
    builder.addCase(analyzeEvidenceAsync.pending, (state) => {
      state.analyzeEvidenceAsyncLoading = "pending";
      state.analyzeEvidenceAsyncError = null;
    });
    builder.addCase(analyzeEvidenceAsync.fulfilled, (state, { payload }) => {
      // Merge results instead of replacing to prevent race conditions
      // when multiple analysis requests are in flight (API + WebSocket)
      payload.results.forEach((newResult: Result) => {
        const existingIndex = state.inspectionResults.findIndex(
          r => r.questionId === newResult.questionId
        );
        if (existingIndex !== -1) {
          // Update existing result for this question
          state.inspectionResults[existingIndex] = newResult;
        } else {
          // Append new result
          state.inspectionResults.push(newResult);
        }
      });
      state.analyzeEvidenceAsyncLoading = "fulfilled";
    });
    builder.addCase(analyzeEvidenceAsync.rejected, (state, { error }) => {
      state.analyzeEvidenceAsyncLoading = "rejected";
      state.analyzeEvidenceAsyncError = miniSerializeError(error);
    });
  },
});

export const {
  setInspectionResults,
  updateInspectionResults,
  setInspectionResult,
  resetCurrentAnalyzedResult,
  setCurrentAnalyzedResult,
  resetInspectionResults,
} = actions;

export default reducer;
